import Axios from "axios";
import useAnalytics from "hooks/analytics";
import { DateTime } from "luxon";
import AppointmentNotAvailableModal from "modals/appointment-not-available";
import { ScreeningFlow, SCREENING_FLOW } from "models/screening-flow";
import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { UserActionEvent } from "utils/analytics/events";
import { setScreeningFlow } from "utils/session-storage";
import { AppointmentCard, Modal } from "../../../components";
import Tabs, { TabInterface as Tab } from "../../../components/tabs";
import useAuthUser from "../../../hooks/auth-user";
import useLanguage from "../../../hooks/language";
import {
    CancelAppointmentModal,
    DoubleFeedbackModal,
    EditAppointmentModal,
    RejectPhoneAppointmentModal,
    ScheduleAppointmentModal,
    SchedulePhoneAppointmentModal,
} from "../../../modals";
import Appointment, {
    getPhoneAppointmentToSchedule,
    isHpv,
} from "../../../models/appointment";
import PhoneAppointment, {
    getLinkedAppointmentDateTime,
} from "../../../models/phone-appointment";
import {
    appointmentWithSatisfactionQuestionnairePrompt,
    isAppointment,
    isUpcoming,
    sortAppointments,
} from "../../../utils/appointment-helpers";
import {
    AppointmentScrollKind,
    getAppointmentElementId,
    scrollToAppointment,
    UPCOMING_APPOINTMENTS_SECTION_ID,
} from "../../../utils/appointment-scroll";
import ENDPOINTS from "../../../utils/endpoints";
import ROUTES from "../../../utils/routes";
import { MyPrEP } from "../my-prep";
import styles from "./my-appointments.module.css";

type AppointmentAction =
    | "edit"
    | "reject"
    | "reschedule"
    | "schedule-retake"
    | "schedule-phone-appointment"
    | "schedule-follow-up-appointment"
    | "feedback";

type AppointmentsLocationState = {
    scrollToAppointmentId?: number;
    scrollToAppointmentKind?: AppointmentScrollKind;
};

interface IMyAppointmentsProperties {
    tabHeaderClassName?: string;
}

export default function MyAppointments(
    props: IMyAppointmentsProperties
): React.ReactElement {
    const { translations, language } = useLanguage();
    const [authUser, setAuthUser] = useAuthUser();
    const { push, replace } = useHistory();
    const location = useLocation<AppointmentsLocationState>();
    const t = translations.appointmentsSection;
    const [analytics] = useAnalytics();

    const modalContentRef = useRef<HTMLDivElement | null>(null);

    const [modalAppointment, setModalAppointment] = useState<Appointment>();
    const [
        modalPhoneAppointment,
        setModalPhoneAppointment,
    ] = useState<PhoneAppointment>();

    const [feedbackModalVisible, setFeedbackModalVisible] = useState(false);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [rejectModalVisible, setRejectModalVisible] = useState(false);
    const [cancelModalVisible, setCancelModalVisible] = useState(false);
    const [
        scheduleAppointmentModalVisible,
        setScheduleAppointmentModalVisible,
    ] = useState(false);
    const [
        schedulePhoneAppointmentModalVisible,
        setSchedulePhoneAppointmentModalVisible,
    ] = useState(false);
    const [
        appointmentNotAvailableModalVisible,
        setAppointmentNotAvailableModalVisible,
    ] = useState(false);
    const [appointmentDate, setAppointmentDate] = useState("");
    const [activeTabIndex, setActiveTabIndex] = useState<number | undefined>(
        undefined
    );

    useEffect(() => {
        if (!authUser) return;

        Axios.put(ENDPOINTS.APPOINTMENTS, {});

        const appointmentNeedImmediateFeedBack = appointmentWithSatisfactionQuestionnairePrompt(
            authUser.appointments
        );
        if (appointmentNeedImmediateFeedBack)
            onClickAppointmentButton(
                appointmentNeedImmediateFeedBack,
                "feedback"
            );
    }, [authUser]);

    const onClickAppointmentButton = (
        appointment: Appointment | PhoneAppointment,
        action: AppointmentAction
    ) => {
        if (isAppointment(appointment)) {
            setModalAppointment(appointment);

            if (action === "schedule-follow-up-appointment") {
                setModalPhoneAppointment(
                    getPhoneAppointmentToSchedule(appointment)
                );
            }
        } else {
            setModalPhoneAppointment(appointment);
        }

        switch (action) {
            case "edit":
                setEditModalVisible(true);
                break;
            case "reject":
                setRejectModalVisible(true);
                break;
            case "reschedule":
                setScheduleAppointmentModalVisible(true);
                break;
            case "schedule-retake":
                setScheduleAppointmentModalVisible(true);
                break;
            case "schedule-phone-appointment":
                setSchedulePhoneAppointmentModalVisible(true);
                break;
            case "schedule-follow-up-appointment":
                setSchedulePhoneAppointmentModalVisible(true);
                break;
            case "feedback":
                analytics
                    ?.cdp()
                    ?.trackEvent(UserActionEvent.AppointmentReviewInitiated);
                setFeedbackModalVisible(true);
                break;
        }
    };

    const onAppointmentChange = async () => {
        const { data } = await Axios.get(ENDPOINTS.ME);
        setAuthUser(data);

        setModalAppointment(undefined);
        setModalPhoneAppointment(undefined);

        setEditModalVisible(false);
        setCancelModalVisible(false);
        setRejectModalVisible(false);
        setScheduleAppointmentModalVisible(false);
        setSchedulePhoneAppointmentModalVisible(false);
        setFeedbackModalVisible(false);
    };

    const onAppointmentNotAvailable = (appointmentDate: string) => {
        setAppointmentDate(appointmentDate);
        setAppointmentNotAvailableModalVisible(true);
    };

    const onClickRetakeQuestionnaireButton = (
        appointment: Appointment | PhoneAppointment
    ) => {
        if (isAppointment(appointment)) {
            let screeningFlow: ScreeningFlow = SCREENING_FLOW.STI;
            if (isHpv(appointment)) {
                screeningFlow = SCREENING_FLOW.HPV;
            }
            setScreeningFlow(screeningFlow);
        }
        push(ROUTES.SCREENING);
    };

    const mapAppointmentToCard = (
        appointment: Appointment | PhoneAppointment
    ) => {
        const scrollKind: AppointmentScrollKind = isAppointment(appointment)
            ? "screening"
            : "phone";

        return (
            <div
                key={`${scrollKind}-${appointment.id}`}
                id={getAppointmentElementId(appointment.id, scrollKind)}
            >
                <AppointmentCard
                    appointment={appointment}
                    onEditClick={() =>
                        onClickAppointmentButton(appointment, "edit")
                    }
                    onRejectClick={() =>
                        onClickAppointmentButton(appointment, "reject")
                    }
                    onRescheduleClick={() =>
                        onClickAppointmentButton(appointment, "reschedule")
                    }
                    onScheduleRetakeClick={() =>
                        onClickAppointmentButton(appointment, "schedule-retake")
                    }
                    onSchedulePhoneAppointmentClick={() =>
                        onClickAppointmentButton(
                            appointment,
                            "schedule-follow-up-appointment"
                        )
                    }
                    onChooseDateClick={() =>
                        onClickAppointmentButton(
                            appointment,
                            "schedule-phone-appointment"
                        )
                    }
                    onFeedbackClick={() =>
                        onClickAppointmentButton(appointment, "feedback")
                    }
                    onRetakeQuestionnaireClick={() =>
                        onClickRetakeQuestionnaireButton(appointment)
                    }
                />
            </div>
        );
    };

    const appointments = authUser
        ? [...authUser.phoneAppointments, ...authUser.appointments]
        : [];

    const upcomingAppointments = appointments
        .filter((app) => isUpcoming(app))
        .sort((a, b) => sortAppointments(a, b, "asc"));
    const pastAppointments = appointments
        .filter((app) => !isUpcoming(app))
        .sort((a, b) => sortAppointments(a, b, "desc"));

    const hasNoAppointment =
        upcomingAppointments.length === 0 && pastAppointments.length === 0;

    const noAppointmentElement = hasNoAppointment ? (
        <div className={styles.noAppointmentHolder}>
            <hr />
            <div className={styles["noAppointmentHolder-content"]}>
                {t.noAppointmentYet}
            </div>
        </div>
    ) : null;

    const upcomingApointmentListElement =
        upcomingAppointments.length > 0 ? (
            <div
                id={UPCOMING_APPOINTMENTS_SECTION_ID}
                className={styles.upcomingAppointmentsSection}
            >
                {upcomingAppointments.map(mapAppointmentToCard)}
            </div>
        ) : null;
    const pastApointmentListElement =
        pastAppointments.length > 0 ? (
            <div>{pastAppointments.map(mapAppointmentToCard)}</div>
        ) : null;

    const modalAppointmentDate = modalAppointment
        ? DateTime.fromISO(modalAppointment.datetime)
        : null;
    const modalAppointmentDateString = modalAppointmentDate
        ? modalAppointmentDate
              .setLocale(language)
              .toLocaleString(DateTime.DATE_MED)
        : null;

    const tabs: Tab[] = [];

    if (upcomingApointmentListElement) {
        tabs.push({
            title: t.upcomingAppointments,
            content: upcomingApointmentListElement,
        });
    }
    if (pastApointmentListElement)
        tabs.push({
            title: t.pastAppointments,
            content: pastApointmentListElement,
        });

    tabs.push({
        title: "PrEp",
        content: <MyPrEP tabHeaderClassName={props.tabHeaderClassName} />,
    });

    useEffect(() => {
        const scrollToAppointmentId = location.state?.scrollToAppointmentId;
        if (!scrollToAppointmentId || !authUser) {
            return;
        }

        const scrollKind =
            location.state?.scrollToAppointmentKind ?? "screening";
        const upcomingTabIndex =
            upcomingAppointments.length > 0 ? 0 : undefined;

        if (
            upcomingTabIndex !== undefined &&
            activeTabIndex !== upcomingTabIndex
        ) {
            setActiveTabIndex(upcomingTabIndex);
            return;
        }

        requestAnimationFrame(() => {
            if (scrollToAppointment(scrollToAppointmentId, scrollKind)) {
                replace(location.pathname, {});
            }
        });
    }, [
        activeTabIndex,
        authUser,
        location.pathname,
        location.state?.scrollToAppointmentId,
        location.state?.scrollToAppointmentKind,
        replace,
        upcomingAppointments.length,
    ]);

    let prepSubtitle;
    if (modalPhoneAppointment?.type === "PREP_ELIGIBILITY") {
        const date = getLinkedAppointmentDateTime(
            modalPhoneAppointment.appointment.id,
            authUser
        );
        if (date) {
            const formatedDate = DateTime.fromISO(date).toFormat(
                "d LLL. yyyy, HH'h'mm"
            );
            prepSubtitle = `${t.rescheduleAppointmentModal.prepSubtitle} ${formatedDate}`;
        }
    }

    return (
        <div className="container">
            <Modal
                title={
                    modalAppointmentDateString
                        ? t.cancellationModal.header(modalAppointmentDateString)
                        : undefined
                }
                visible={editModalVisible}
                onVisibilityChange={setEditModalVisible}
            >
                <EditAppointmentModal
                    onRescheduleClick={() =>
                        setScheduleAppointmentModalVisible(true)
                    }
                    onCancelClick={() => setCancelModalVisible(true)}
                    canRescheduleFreely={modalAppointment?.canRescheduleFreely}
                />
            </Modal>

            <Modal
                title={t.cancellationConfirmModal.title}
                visible={cancelModalVisible}
                onVisibilityChange={setCancelModalVisible}
            >
                <CancelAppointmentModal
                    onClose={() => setCancelModalVisible(false)}
                    onConfirm={onAppointmentChange}
                    appointment={modalAppointment}
                />
            </Modal>

            <Modal
                title={t.cancellationConfirmModal.phoneTitle}
                visible={rejectModalVisible}
                onVisibilityChange={setRejectModalVisible}
            >
                <RejectPhoneAppointmentModal
                    onClose={() => setRejectModalVisible(false)}
                    onConfirm={onAppointmentChange}
                    appointment={modalPhoneAppointment}
                />
            </Modal>

            <Modal
                title={t.rescheduleAppointmentModal.title}
                visible={scheduleAppointmentModalVisible}
                onVisibilityChange={setScheduleAppointmentModalVisible}
                contentRef={modalContentRef}
            >
                <ScheduleAppointmentModal
                    isVisible={scheduleAppointmentModalVisible}
                    onClose={() => setScheduleAppointmentModalVisible(false)}
                    onConfirm={onAppointmentChange}
                    onAppointmentNotAvailable={onAppointmentNotAvailable}
                    appointment={modalAppointment}
                    modalContentRef={modalContentRef}
                />
            </Modal>

            <Modal
                title={t.rescheduleAppointmentModal.title}
                subtitle={prepSubtitle}
                visible={schedulePhoneAppointmentModalVisible}
                onVisibilityChange={setSchedulePhoneAppointmentModalVisible}
            >
                <SchedulePhoneAppointmentModal
                    onClose={() =>
                        setSchedulePhoneAppointmentModalVisible(false)
                    }
                    onConfirm={onAppointmentChange}
                    appointment={modalPhoneAppointment}
                />
            </Modal>

            <DoubleFeedbackModal
                modalAppointment={modalAppointment}
                visible={feedbackModalVisible}
                onVisibilityChange={setFeedbackModalVisible}
            />

            <AppointmentNotAvailableModal
                appointmentDate={appointmentDate}
                visible={appointmentNotAvailableModalVisible}
                onClose={() => setAppointmentNotAvailableModalVisible(false)}
            />

            {hasNoAppointment && noAppointmentElement}

            {tabs.length > 0 && (
                <Tabs
                    tabHeaderClassName={props.tabHeaderClassName}
                    currentIndex={
                        hasNoAppointment ? undefined : activeTabIndex ?? 0
                    }
                    onChange={(_, index) => setActiveTabIndex(index)}
                    tabs={tabs}
                />
            )}
        </div>
    );
}
